const getLecturerFromEntry = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  if ('lecturer' in entry) {
    return entry.lecturer;
  }

  return entry;
};

const normalizeModuleLecturerEntries = (lecturers) => {
  if (!lecturers) {
    return [];
  }

  return Array.isArray(lecturers) ? lecturers.filter(Boolean) : [lecturers];
};

const normalizeModuleLecturers = (lecturers) =>
  normalizeModuleLecturerEntries(lecturers)
    .map((entry) => getLecturerFromEntry(entry))
    .filter(Boolean);

const getModuleLecturerIds = (lecturers) =>
  normalizeModuleLecturers(lecturers)
    .map((lecturer) => (typeof lecturer === 'object' ? lecturer?.id : lecturer))
    .filter(Boolean);

const getModuleLecturerNames = (lecturers) =>
  normalizeModuleLecturers(lecturers)
    .map((lecturer) => {
      if (typeof lecturer === 'object') {
        return lecturer.fullName || `${lecturer.firstName || ''} ${lecturer.lastName || ''}`.trim();
      }

      return '';
    })
    .filter(Boolean);

const getModuleLecturersForDisplay = (lecturers) =>
  normalizeModuleLecturerEntries(lecturers)
    .map((entry) => {
      const lecturer = getLecturerFromEntry(entry);

      if (!lecturer || typeof lecturer !== 'object') {
        return null;
      }

      return {
        ...lecturer,
        bio: entry.bio,
      };
    })
    .filter(Boolean);

const moduleHasLecturer = (lecturers, lecturerId) =>
  getModuleLecturerIds(lecturers).some((id) => String(id) === String(lecturerId));

export {
  getLecturerFromEntry,
  getModuleLecturerIds,
  getModuleLecturerNames,
  getModuleLecturersForDisplay,
  moduleHasLecturer,
  normalizeModuleLecturerEntries,
  normalizeModuleLecturers,
};

const getLecturerId = (lecturer) => (typeof lecturer === 'object' ? lecturer?.id : lecturer);

const getEducationLecturerIds = (modules) => [
  ...new Set(
    (modules || [])
      .filter((module) => module && typeof module === 'object')
      .flatMap((module) => getModuleLecturerIds(module.lecturers))
      .map(String)
  ),
];

// Lecturers from all modules, deduped (first module's bio wins), sorted by the
// education's manual order. Lecturers missing from the order keep module order.
const getEducationLecturersForDisplay = (modules, lecturerOrder) => {
  const seen = new Set();
  const lecturers = (modules || [])
    .filter((module) => module && typeof module === 'object')
    .flatMap((module) => getModuleLecturersForDisplay(module.lecturers))
    .filter((lecturer) => {
      const id = String(lecturer.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

  const order = (lecturerOrder || []).map((lecturer) => String(getLecturerId(lecturer)));
  const rank = (lecturer) => {
    const index = order.indexOf(String(lecturer.id));
    return index === -1 ? order.length : index;
  };

  return lecturers
    .map((lecturer, index) => ({ lecturer, index }))
    .sort((a, b) => rank(a.lecturer) - rank(b.lecturer) || a.index - b.index)
    .map(({ lecturer }) => lecturer);
};

export { getEducationLecturerIds, getEducationLecturersForDisplay };
